/** B-08 — 12 order tracking states (frontend) */

export const TRACKING_STATES = [
  'PENDING',
  'ACCEPTED',
  'PACKING',
  'DISPATCH_PENDING',
  'DISPATCHED',
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'COMPLETED',
  'DISPUTED',
  'CANCELLED',
  'REASSIGNED',
];

export const STATE_LABELS = {
  PENDING: 'Order placed — seller confirming',
  ACCEPTED: 'Seller accepted — packing begins',
  PACKING: 'Packing in progress',
  DISPATCH_PENDING: 'Dispatch photo under review',
  DISPATCHED: 'Handed to carrier — tracking active',
  IN_TRANSIT: 'In transit',
  OUT_FOR_DELIVERY: 'Out for delivery today',
  DELIVERED: 'Delivered — 72h claim window',
  COMPLETED: 'Order complete — warranty active',
  DISPUTED: 'Claim under review',
  CANCELLED: 'Order cancelled',
  REASSIGNED: 'Reassigned to verified seller',
};

export const LINEAR_FLOW = [
  'PENDING',
  'ACCEPTED',
  'PACKING',
  'DISPATCH_PENDING',
  'DISPATCHED',
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'COMPLETED',
];

export function getStateIndex(state) {
  return LINEAR_FLOW.indexOf(state);
}

export function isTerminalState(state) {
  return ['CANCELLED', 'DISPUTED', 'REASSIGNED'].includes(state);
}

export function formatCountdown(ms) {
  if (ms <= 0) return '0:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function timelineNodeStatus(stateKey, currentState) {
  if (stateKey === currentState) return 'current';
  if (isTerminalState(currentState)) {
    if (stateKey === currentState) return 'current';
    const idx = getStateIndex(stateKey);
    if (idx >= 0) return idx <= 0 ? 'done' : 'future';
    return 'future';
  }
  const curIdx = getStateIndex(currentState);
  const idx = getStateIndex(stateKey);
  if (idx < 0) return 'future';
  if (idx < curIdx) return 'done';
  if (idx === curIdx) return 'current';
  return 'future';
}

export function showCarrierFromState(state) {
  return ['DISPATCHED', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELIVERED', 'COMPLETED'].includes(state);
}

export const LEGACY_STATUS_TO_TRACKING = {
  'Order Placed': 'PENDING',
  'Pending Payment': 'PENDING',
  Confirmed: 'ACCEPTED',
  'Processing Item': 'PACKING',
  Packed: 'PACKING',
  'Handed to Courier': 'DISPATCHED',
  'Shipment In Transit': 'IN_TRANSIT',
  'Out for Delivery': 'OUT_FOR_DELIVERY',
  'Delivery Completed': 'DELIVERED',
  Cancelled: 'CANCELLED',
  Returned: 'DISPUTED',
};

export function resolveTrackingState(order = {}) {
  if (order.trackingState && TRACKING_STATES.includes(order.trackingState)) {
    return order.trackingState;
  }
  return LEGACY_STATUS_TO_TRACKING[order.status] || 'PENDING';
}

export function getTrackingProgressPercent(state) {
  if (state === 'CANCELLED' || state === 'DISPUTED') return 100;
  if (state === 'REASSIGNED') return 20;
  const idx = getStateIndex(state);
  if (idx < 0) return 8;
  return Math.round(((idx + 1) / LINEAR_FLOW.length) * 100);
}

export function getTrackingBadgeClass(state) {
  if (state === 'CANCELLED') return 'bg-red-950/50 text-red-300 border-red-800';
  if (state === 'DISPUTED') return 'bg-orange-950/50 text-orange-300 border-orange-800';
  if (state === 'DELIVERED' || state === 'COMPLETED') return 'bg-emerald-950/50 text-emerald-300 border-emerald-800';
  if (showCarrierFromState(state)) return 'bg-blue-950/50 text-blue-300 border-blue-800';
  return 'bg-amber-950/50 text-amber-300 border-amber-800';
}

const PRE_DISPATCH_STATES = new Set([
  'PENDING',
  'ACCEPTED',
  'PACKING',
  'DISPATCH_PENDING',
  'REASSIGNED',
]);

/** Pre-dispatch buyer cancel (Scenario 3) — matches backend canCancel */
export function canCancelOrder(order = {}) {
  const state = resolveTrackingState(order);
  if (state === 'CANCELLED') return false;
  return PRE_DISPATCH_STATES.has(state);
}

/** In-transit buyer cancel (Scenario 5) */
export function canCancelInTransit(order = {}) {
  return resolveTrackingState(order) === 'IN_TRANSIT';
}

/** B-09 — buyer can raise claim only after delivery confirmation within 72h window */
export function canRaiseClaim(order = {}) {
  const state = resolveTrackingState(order);
  if (state !== 'DELIVERED') return false;
  const ends = order.claimWindowEndsAt
    ? new Date(order.claimWindowEndsAt).getTime()
    : null;
  if (!ends) return false;
  if (Date.now() >= ends) return false;
  return Boolean(order.otpConfirmedAt || order.deliveryAutoConfirmedAt || order.deliveryConfirmedAt);
}
